import React, { useEffect, useState } from 'react';
import { Input } from '@/shadcn/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shadcn/components/ui/select';
import { Label } from '@/shadcn/components/ui/label';
import { cn } from '@/shadcn/lib/utils';

interface InlineTimeSelectorProps {
  value: number;
  unit: string;
  onChange: (value: number, unit: string) => void;
  className?: string;
}

export const InlineTimeSelector: React.FC<InlineTimeSelectorProps> = ({
  value,
  unit,
  onChange,
  className,
}) => {
  const [localValue, setLocalValue] = useState(value.toString());
  const [localUnit, setLocalUnit] = useState(unit);

  useEffect(() => {
    setLocalValue(value.toString());
    setLocalUnit(unit);
  }, [value, unit]);

  const timeUnits = [
    { value: 'hours', label: 'hours' },
    { value: 'days', label: 'days' },
  ];

  const handleValueChange = (newValue: string) => {
    // Allow decimal numbers up to 4 decimal places (e.g., 1.5, 0.1, .5, 0.0001, 4.5, 3.2)
    if (/^\d*\.?\d{0,4}$/.test(newValue) && newValue !== '.') {
      setLocalValue(newValue);
      const numValue = newValue === '' ? 1 : parseFloat(newValue);
      onChange(numValue, localUnit);
    }
  };

  const handleUnitChange = (newUnit: string) => {
    setLocalUnit(newUnit);
    const numValue = localValue === '' || localValue === '.' ? 1 : parseFloat(localValue);
    onChange(numValue, newUnit);
  };

  return (
    <div className="flex flex-col items-start justify-center gap-2 p-3 relative bg-white rounded-2xl border border-solid border-gray-200">
      <Label className="relative w-fit font-medium text-gray-600 text-sm whitespace-nowrap">
        Wait for
      </Label>

      <div className={cn(
        'relative flex w-full hover:outline hover:outline-1 hover:outline-gray-300 focus-within:outline focus-within:outline-1 focus-within:outline-gray-600 rounded-lg',
        className
      )}
      >
        <div className="relative flex-1">
          <Input
            type="text"
            value={localValue}
            onChange={(e) => handleValueChange(e.target.value)}
            className="rounded-r-none border-r-0 focus-visible:ring-0 focus-visible:ring-offset-0 hover:border-input text-gray-600 font-normal text-base"
            placeholder="1"
            step="0.1"
          />
        </div>
        <Select value={localUnit} onValueChange={handleUnitChange}>
          <SelectTrigger className="w-[100px] rounded-l-none border-l-0 focus:ring-0 focus:ring-offset-0 hover:border-input flex items-center justify-end gap-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {timeUnits.map((timeUnit) => (
              <SelectItem key={timeUnit.value} value={timeUnit.value}>
                {timeUnit.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
